import React, { FC, memo, useCallback, useState } from 'react';
import { Invoice, InvoiceCard } from 'entities/Invoice';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Button } from 'shared/ui/Button';
import { UserPermissions } from 'entities/User';
import { Contract, useIssueContractInvoiceMutation } from 'entities/Contract';
import { showNotification } from 'app/providers/NotificationsProvider';
import { ConfirmModal } from 'shared/ui/ConfirmModal';
import { useCheckPermission } from 'shared/utils/hooks/useCheckPermission';

interface InvoiceTabProps {
  invoices: Invoice[];
  contract?: Contract;
}

export const InvoiceTab: FC<InvoiceTabProps> = memo((props) => {
  const { invoices, contract } = props;

  const { t } = useAppTranslation('contracts');

  const [isOpenedModal, setOpenedModal] = useState(false);

  const openModal = useCallback((): void => {
    setOpenedModal(true);
  }, []);

  const closeModal = useCallback((): void => {
    setOpenedModal(false);
  }, []);

  const [issueContractInvoice, { isLoading }] = useIssueContractInvoiceMutation();

  const isForbiddenIssueInvoice = useCheckPermission(UserPermissions.ISSUE_NEXT_PERIOD_INVOICE);

  const unpaidInvoices = invoices.filter(({ debt }) => debt !== 0);
  const paidInvoices = invoices.filter(({ debt }) => debt === 0);

  const handleIssueContractInvoice = useCallback(async (): Promise<void> => {
    try {
      if (contract?.contractId) {
        await issueContractInvoice(contract.contractId).unwrap();
        showNotification('info', t('Success', { ns: 'common' }), t('Invoice has been successfully issued'));
      }
    } catch (error: CustomAny) {
      showNotification('error', t('Error', { ns: 'common' }), t('Invoice issue error'));
    } finally {
      closeModal();
    }
  }, [closeModal, contract?.contractId, issueContractInvoice, t]);

  return (
    <div>
      <div className="space-y-3">
        {!isForbiddenIssueInvoice && contract && (
          <Button theme="outlined" onClick={openModal} isDisabled={!!contract.leaveDate}>
            {t('Issue next invoice')}
          </Button>
        )}
        <div className="font-semibold text-lg pt-7 pb-4">{t('Active invoices')}</div>
        {unpaidInvoices.map((invoice) => (
          <InvoiceCard key={invoice.invoiceId} invoice={invoice} />
        ))}
      </div>
      {Boolean(paidInvoices.length) && (
        <>
          <div className="font-semibold text-lg pt-7 pb-4">{t('History')}</div>
          <div className="space-y-3">
            {paidInvoices.map((invoice) => (
              <InvoiceCard key={invoice.invoiceId} invoice={invoice} />
            ))}
          </div>
        </>
      )}
      <ConfirmModal
        isOpened={isOpenedModal}
        title={t('Issue next contract invoice')}
        description={t('Are you sure you want to issue new service period of this contract ?')}
        isLoading={isLoading}
        onOk={handleIssueContractInvoice}
        onCancel={closeModal}
      />
    </div>
  );
});
